<template>
    <div class="note-editor">

        <div class="note-editor-loading" v-if="loading">
            <div class="note-editor-loading-toolbar">
                <div class="note-editor-loading-pill" style="width: 28px"></div>
                <div class="note-editor-loading-pill" style="width: 72px"></div>
                <div class="note-editor-loading-pill" style="width: 64px"></div>
            </div>
            <div class="note-editor-loading-body">
                <div class="note-editor-loading-line note-editor-loading-line--title" style="width: 40%"></div>
                <div class="note-editor-loading-gap"></div>
                <div class="note-editor-loading-line" style="width: 95%"></div>
                <div class="note-editor-loading-line" style="width: 80%"></div>
                <div class="note-editor-loading-line" style="width: 88%"></div>
                <div class="note-editor-loading-line" style="width: 55%"></div>
                <div class="note-editor-loading-gap"></div>
                <div class="note-editor-loading-line" style="width: 92%"></div>
                <div class="note-editor-loading-line" style="width: 76%"></div>
                <div class="note-editor-loading-line" style="width: 45%"></div>
            </div>
        </div>

        <div class="note-editor-empty" v-else-if="!note">
            <div class="note-editor-empty-icon">📝</div>
            <div class="note-editor-empty-title">{{ $t('pages.module.NOTE.editor.emptyTitle') }}</div>
            <div class="note-editor-empty-desc">{{ $t('pages.module.NOTE.editor.emptyDescription') }}</div>
        </div>

        <template v-else>

            <div class="note-editor-toolbar" v-if="!note._isTrashed">
                <div class="note-editor-toolbar-left">

                    <el-popover v-if="canEditFull" trigger="click" :width="196" placement="bottom-start" popper-class="note-app-popper note-color-popper" :teleported="true">
                        <template #reference>
                            <button class="toolbar-chip" :title="$t('pages.module.NOTE.editor.color')">
                                <span class="toolbar-chip-color" :class="{'toolbar-chip-color--none': !formData.color}" :style="formData.color ? {background: formData.color} : {}">
                                    <svg v-if="!formData.color" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><line x1="4" y1="4" x2="20" y2="20"/></svg>
                                </span>
                                <svg class="toolbar-chip-arrow" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg>
                            </button>
                        </template>
                        <div class="color-popover-grid">
                            <div class="color-popover-item color-popover-item--none"
                                 :class="{active: !formData.color}"
                                 @click="formData.color = null"
                                 :title="$t('pages.module.NOTE.editor.noColor')">
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="4" y1="4" x2="20" y2="20"/></svg>
                            </div>
                            <div v-for="color in noteColors" :key="color.id"
                                 class="color-popover-item"
                                 :class="{active: formData.color === color.hex}"
                                 :style="{background: color.hex}"
                                 :title="color.label"
                                 @click="formData.color = color.hex">
                                <svg v-if="formData.color === color.hex" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3"><polyline points="20 6 9 17 4 12"/></svg>
                            </div>
                        </div>
                    </el-popover>

                    <template v-if="!note._isTemp">
                        <span v-if="canEditFull" class="toolbar-separator"></span>

                        <el-select v-if="canEditFull" v-model="formData.category_id" :placeholder="$t('pages.module.NOTE.editor.noCategory')" clearable filterable size="small" class="toolbar-select" popper-class="note-app-popper" :teleported="true">
                            <el-option v-for="cat in flatCategories" :key="cat.id" :label="cat.label" :value="cat.id"/>
                        </el-select>

                        <el-select v-if="canEditFull" v-model="formData.tag_ids" :placeholder="$t('pages.module.NOTE.editor.tags')" multiple collapse-tags collapse-tags-tooltip clearable size="small" class="toolbar-select" popper-class="note-app-popper" :teleported="true">
                            <el-option v-for="tag in tags" :key="tag.id" :label="tag.name" :value="tag.id"/>
                        </el-select>
                    </template>
                </div>

                <div v-if="!compact" class="note-editor-toolbar-right">

                    <template v-if="!note._isTemp">
                        <button class="toolbar-icon-btn" :class="{'toolbar-icon-btn--pinned': note.is_pinned}" @click="$emit('pin', note)" :title="note.is_pinned ? $t('pages.module.NOTE.editor.unpin') : $t('pages.module.NOTE.editor.pin')">
                            <svg width="15" height="15" viewBox="0 0 24 24" :fill="note.is_pinned ? 'currentColor' : 'none'" stroke="currentColor" stroke-width="2"><path d="M9 4v6l-2 4h5v6l1-1 1 1v-6h5l-2-4V4"/><line x1="8" y1="4" x2="16" y2="4"/></svg>
                        </button>

                        <el-popover :visible="showReminderPopover" :width="280" placement="bottom-end" popper-class="note-app-popper note-reminder-popper" :teleported="true">
                            <template #reference>
                                <button ref="reminderBtn" class="toolbar-icon-btn" :class="{'toolbar-icon-btn--reminder-active': hasReminder}" @click="showReminderPopover = !showReminderPopover" :title="$t('pages.module.NOTE.editor.reminder')">
                                    <span class="reminder-icon-wrapper">
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                                        <span v-if="hasReminder" class="reminder-dot"></span>
                                    </span>
                                </button>
                            </template>
                            <div class="reminder-popover">
                                <div class="reminder-popover-title">{{ $t('pages.module.NOTE.editor.reminder') }}</div>
                                <div class="reminder-popover-field">
                                    <label>{{ $t('pages.module.NOTE.editor.reminderEventAt') }}</label>
                                    <DateTimePicker v-model="reminderEventAt" type="datetime" :placeholder="$t('pages.module.NOTE.editor.reminderNoDate')" :disabled-date="disablePastDate" :teleported="true"/>
                                </div>
                                <div class="reminder-popover-field">
                                    <label>{{ $t('pages.module.NOTE.editor.reminderReminderAt') }}</label>
                                    <DateTimePicker v-model="reminderReminderAt" type="datetime" :placeholder="$t('pages.module.NOTE.editor.reminderNoDate')" :disabled="!reminderEventAt" :disabled-date="disableReminderDate" :max="reminderEventAt" clearable :teleported="true"/>
                                </div>
                                <div class="reminder-popover-actions">
                                    <button class="reminder-save-btn" :disabled="!reminderEventAt" @click="handleReminderSave">{{ $t('pages.module.NOTE.editor.reminderSave') }}</button>
                                    <button v-if="hasReminder" class="reminder-delete-btn" @click="handleReminderDelete">{{ $t('pages.module.NOTE.editor.reminderDelete') }}</button>
                                </div>
                            </div>
                        </el-popover>

                        <button v-if="canEditFull" class="toolbar-icon-btn" :class="{'toolbar-icon-btn--active': showSettings}" @click="showSettings = !showSettings" :title="$t('pages.module.NOTE.editor.settings')">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
                        </button>
                    </template>

                    <button v-if="note.is_owner" class="toolbar-icon-btn toolbar-icon-btn--delete" @click="$emit('delete', note)" :title="$t('pages.module.NOTE.editor.delete')">
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                    </button>
                </div>
            </div>


            <div class="note-editor-content" :class="{'read-only': !note.can_edit}" :key="editorKey">
                <div v-if="!note.can_edit" class="note-editor-readonly-badge">{{ $t('pages.module.NOTE.editor.readOnly') }}</div>
                <CustomCkeditor ref="ckeditor" v-if="note.can_edit" :model="formData.content" @updateModel="val => formData.content = val" @liveUpdate="onLiveUpdate" :defer-upload="true" :upload-enabled="!note._isTemp"/>
                <div v-else class="note-editor-html-view" v-html="formData.content"></div>
            </div>


            <div class="note-editor-reminder-panel" v-if="compact && showReminderPopover && !note._isTemp">
                <div class="reminder-panel-header">
                    <span class="reminder-panel-title">{{ $t('pages.module.NOTE.editor.reminder') }}</span>
                    <button class="reminder-panel-close" @click="showReminderPopover = false">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                </div>
                <div class="reminder-popover-field">
                    <label>{{ $t('pages.module.NOTE.editor.reminderEventAt') }}</label>
                    <DateTimePicker v-model="reminderEventAt" type="datetime" :placeholder="$t('pages.module.NOTE.editor.reminderNoDate')" :disabled-date="disablePastDate" :auto-modal="true" :teleported="true"/>
                </div>
                <div class="reminder-popover-field">
                    <label>{{ $t('pages.module.NOTE.editor.reminderReminderAt') }}</label>
                    <DateTimePicker v-model="reminderReminderAt" type="datetime" :placeholder="$t('pages.module.NOTE.editor.reminderNoDate')" :disabled="!reminderEventAt" :disabled-date="disableReminderDate" :max="reminderEventAt" clearable :auto-modal="true" :teleported="true"/>
                </div>
                <div class="reminder-popover-actions">
                    <button class="reminder-save-btn" :disabled="!reminderEventAt" @click="handleReminderSave">{{ $t('pages.module.NOTE.editor.reminderSave') }}</button>
                    <button v-if="hasReminder" class="reminder-delete-btn" @click="handleReminderDelete">{{ $t('pages.module.NOTE.editor.reminderDelete') }}</button>
                </div>
            </div>

            <div class="note-editor-settings" v-if="showSettings && !note._isTemp">

                <div class="settings-section">
                    <div class="settings-section-label">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                        {{ $t('pages.module.NOTE.editor.visibility') }}
                    </div>
                    <div class="settings-segmented" :class="{'settings-segmented--disabled': !canEditFull}">
                        <button class="settings-segmented-btn" :class="{active: formData.visibility_id === 1}" @click="canEditFull && (formData.visibility_id = 1)">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                            <span class="segmented-label-full">{{ $t('pages.module.NOTE.editor.visibilityPrivate') }}</span>
                            <span class="segmented-label-short">{{ $t('pages.module.NOTE.editor.visibilityPrivateShort') }}</span>
                        </button>
                        <button class="settings-segmented-btn" :class="{active: formData.visibility_id === 2}" @click="canEditFull && (formData.visibility_id = 2)">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                            <span class="segmented-label-full">{{ $t('pages.module.NOTE.editor.visibilityGroup.' + $handler.platformType) }}</span>
                            <span class="segmented-label-short">{{ $t('pages.module.NOTE.editor.visibilityGroupShort.' + $handler.platformType) }}</span>
                        </button>
                        <button class="settings-segmented-btn" :class="{active: formData.visibility_id === 3}" @click="canEditFull && (formData.visibility_id = 3)">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                            <span class="segmented-label-full">{{ $t('pages.module.NOTE.editor.visibilitySelected') }}</span>
                            <span class="segmented-label-short">{{ $t('pages.module.NOTE.editor.visibilitySelectedShort') }}</span>
                        </button>
                    </div>
                    <div class="settings-sub-select" v-if="formData.visibility_id === 3">
                        <el-select v-model="formData.viewer_ids" :placeholder="$t('pages.module.NOTE.editor.viewers')" multiple filterable size="small" :disabled="!canEditFull" popper-class="note-app-popper" :teleported="true" :class="{'settings-select--error': validationErrors.viewer}">
                            <el-option v-for="o in officerList" :key="o.id" :label="o.name" :value="o.id"/>
                        </el-select>
                        <div v-if="validationErrors.viewer" class="settings-validation-error">{{ $t('pages.module.NOTE.editor.viewerRequired') }}</div>
                    </div>
                </div>


                <div class="settings-section" v-if="formData.visibility_id !== 1">
                    <div class="settings-section-label">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        {{ $t('pages.module.NOTE.editor.editPermission') }}
                    </div>
                    <div class="settings-segmented" :class="{'settings-segmented--disabled': !canEditFull}">
                        <button class="settings-segmented-btn" :class="{active: formData.edit_permission_id === 1}" @click="canEditFull && (formData.edit_permission_id = 1)">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                            <span class="segmented-label-full">{{ $t('pages.module.NOTE.editor.editPermissionOwner') }}</span>
                            <span class="segmented-label-short">{{ $t('pages.module.NOTE.editor.editPermissionOwnerShort') }}</span>
                        </button>
                        <button class="settings-segmented-btn" :class="{active: formData.edit_permission_id === 2}" @click="canEditFull && (formData.edit_permission_id = 2)">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                            <span class="segmented-label-full">{{ $t('pages.module.NOTE.editor.editPermissionAll') }}</span>
                            <span class="segmented-label-short">{{ $t('pages.module.NOTE.editor.editPermissionAllShort') }}</span>
                        </button>
                        <button class="settings-segmented-btn" :class="{active: formData.edit_permission_id === 3}" @click="canEditFull && (formData.edit_permission_id = 3)">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                            <span class="segmented-label-full">{{ $t('pages.module.NOTE.editor.editPermissionSelected') }}</span>
                            <span class="segmented-label-short">{{ $t('pages.module.NOTE.editor.editPermissionSelectedShort') }}</span>
                        </button>
                    </div>
                    <div class="settings-sub-select" v-if="formData.edit_permission_id === 3">
                        <el-select v-model="formData.editor_ids" :placeholder="$t('pages.module.NOTE.editor.editors')" multiple filterable size="small" :disabled="!canEditFull" popper-class="note-app-popper" :teleported="true" :class="{'settings-select--error': validationErrors.editor}">
                            <el-option v-for="o in editorCandidates" :key="o.id" :label="o.name" :value="o.id"/>
                        </el-select>
                        <div v-if="validationErrors.editor" class="settings-validation-error">{{ $t('pages.module.NOTE.editor.editorRequired') }}</div>
                    </div>
                </div>


                <div class="settings-section settings-section--revisions" v-if="note.is_owner && note.revisions && note.revisions.length > 0">
                    <div class="settings-section-label settings-section-label--clickable" @click="showRevisions = !showRevisions">
                        <div class="settings-section-label-left">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                            {{ $t('pages.module.NOTE.editor.revisions') }} ({{ note.revisions.length }})
                        </div>
                        <span class="revisions-toggle">{{ showRevisions ? '▾' : '▸' }}</span>
                    </div>
                    <div v-if="showRevisions" class="editor-revisions-list">
                        <div v-for="rev in note.revisions" :key="rev.id" class="editor-revision-item" @click="previewRevision(rev)">
                            <span class="revision-date">{{ $moment(rev.created_at).format('D.MM.YYYY HH:mm') }}</span>
                            <span class="revision-user">{{ rev.user?.full_name || '' }}</span>
                        </div>
                    </div>
                </div>
            </div>


            <div class="note-editor-actions" v-if="note.can_edit && !note._isTrashed">
                <button class="editor-save-btn" @click="handleSave" :disabled="saving || uploading">
                    <span v-if="saving" class="editor-save-spinner"></span>
                    {{ $t('pages.module.NOTE.editor.save') }}
                </button>
            </div>


            <BaseModal :visible="!!revisionPreview" width="640px" max-width="92vw" max-height="82vh"
                       content-class="note-revision-modal-content" @close="revisionPreview = null">
                        <div class="note-revision-modal-header">
                            <div class="note-revision-modal-header-left">
                                <div class="note-revision-modal-title">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                    {{ $t('pages.module.NOTE.editor.revisionDetail') }}
                                </div>
                                <div class="note-revision-modal-meta">
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                                    <span class="note-revision-modal-meta-text">{{ revisionPreview.user?.full_name || '-' }}</span>
                                    <span class="note-revision-modal-meta-dot"></span>
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                                    <span class="note-revision-modal-meta-text">{{ $moment(revisionPreview.created_at).format('D MMMM YYYY, HH:mm') }}</span>
                                </div>
                            </div>
                            <button @click="revisionPreview = null" class="note-revision-modal-close">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                            </button>
                        </div>
                        <div class="note-revision-modal-body">
                            <div class="note-revision-modal-field">
                                <div class="note-revision-modal-field-label">{{ $t('pages.module.NOTE.editor.revisionTitle') }}</div>
                                <div class="note-revision-modal-field-value">{{ revisionPreview.title || '-' }}</div>
                            </div>
                            <div class="note-revision-modal-field">
                                <div class="note-revision-modal-field-label">{{ $t('pages.module.NOTE.editor.revisionContent') }}</div>
                                <div class="note-revision-modal-field-value note-revision-modal-field-value--html" v-html="revisionPreview.content"></div>
                            </div>
                        </div>
            </BaseModal>
        </template>
    </div>
</template>

<script>
import CustomCkeditor from "@/components/custom-ckeditor"
import DateTimePicker from "@/components/common/date-time-picker/DateTimePicker.vue"
import BaseModal from "@/components/common/base-modal/BaseModal.vue"

const NOTE_COLORS = [
    {id: 'red', hex: '#FF3B30', label: 'Kırmızı'},
    {id: 'orange', hex: '#FF9500', label: 'Turuncu'},
    {id: 'yellow', hex: '#FFCC00', label: 'Sarı'},
    {id: 'green', hex: '#34C759', label: 'Yeşil'},
    {id: 'blue', hex: '#007AFF', label: 'Mavi'},
    {id: 'purple', hex: '#AF52DE', label: 'Mor'},
    {id: 'pink', hex: '#FF2D55', label: 'Pembe'},
    {id: 'gray', hex: '#8E8E93', label: 'Gri'},
]

export default {
    name: 'NoteEditor',
    props: {
        note: {type: Object, default: null},
        loading: {type: Boolean, default: false},
        categories: {type: Array, default: () => []},
        tags: {type: Array, default: () => []},
        officers: {type: Array, default: () => []},
        compact: {type: Boolean, default: false},
        uploading: {type: Boolean, default: false},
    },
    components: {CustomCkeditor, DateTimePicker, BaseModal},
    emits: ['save', 'delete', 'pin', 'colorChange', 'reminderSave', 'reminderDelete', 'draftUpdate'],
    data() {
        return {
            noteColors: NOTE_COLORS,
            formData: this.createFormData(this.note),
            showSettings: false,
            showRevisions: false,
            revisionPreview: null,
            saving: false,
            ignoreColorChange: false,
            editorKey: 0,
            showReminderPopover: false,
            reminderEventAt: this.note?.agenda?.event_at || null,
            reminderReminderAt: this.note?.agenda?.reminder_at || null,
            validationErrors: {},
        }
    },
    computed: {
        canEditFull() {
            return this.note && this.note.is_owner
        },
        hasReminder() {
            return !!this.note?.has_agenda
        },
        currentUserId() {
            return this.$store.getters.currentUser?.id || null
        },
        officerList() {
            return this.officers.map(o => this.$handler.mapMember(o)).filter(o => o.id && o.name && o.id !== this.currentUserId)
        },
        editorCandidates() {
            if (this.formData.visibility_id === 2) return this.officerList
            return this.officerList.filter(o => this.formData.viewer_ids.includes(o.id))
        },
        flatCategories() {
            const flat = []
            const flatten = (items, prefix = '') => {
                for (const item of items) {
                    flat.push({id: item.id, label: prefix + item.name})
                    if (item.children && item.children.length) {
                        flatten(item.children, prefix + item.name + ' / ')
                    }
                }
            }
            flatten(this.categories)
            return flat
        },
    },
    watch: {
        note: {
            handler(newNote, oldNote) {
                if (oldNote?._isTemp && newNote && !newNote._isTemp) {
                    return
                }
                if (newNote?._isTemp && oldNote?._isTemp && newNote?.id === oldNote?.id) {
                    return
                }
                this.editorKey++
                this.ignoreColorChange = true
                this.formData = this.createFormData(newNote)
                this.$nextTick(() => { this.ignoreColorChange = false })
                this.showRevisions = false
                this.revisionPreview = null
                this.reminderEventAt = newNote?.agenda?.event_at || null
                this.reminderReminderAt = newNote?.agenda?.reminder_at || null
                this.showReminderPopover = false
                this.validationErrors = {}
            },
            deep: false,
        },
        'formData.visibility_id'(val) {
            if (val === 1) {
                this.formData.edit_permission_id = 1
                this.formData.editor_ids = []
            }
        },
        'formData.viewer_ids'(val) {
            if (val && val.length > 0) delete this.validationErrors.viewer
        },
        'formData.editor_ids'(val) {
            if (val && val.length > 0) delete this.validationErrors.editor
        },
        editorCandidates(candidates) {
            const validIds = new Set(candidates.map(c => c.id))
            const filtered = this.formData.editor_ids.filter(id => validIds.has(id))
            if (filtered.length !== this.formData.editor_ids.length) {
                this.formData.editor_ids = filtered
            }
        },
        'formData.color'(newColor, oldColor) {
            if (this.note && oldColor !== undefined && !this.ignoreColorChange && this.canEditFull) {
                this.$emit('colorChange', {id: this.note.id, color: newColor})
            }
        },
        'formData.content'(newVal) {
            if (this.note?._isTemp) {
                this.$emit('draftUpdate', {id: this.note.id, formData: {...this.formData}})
                return
            }
        },
        reminderEventAt(newVal) {
            if (this.reminderReminderAt && newVal && this.reminderReminderAt > newVal) {
                this.reminderReminderAt = null
            }
        },
        showReminderPopover(val) {
            if (val && !this.compact) {
                setTimeout(() => document.addEventListener('mousedown', this._onReminderOutsideClick, true), 0)
            } else {
                document.removeEventListener('mousedown', this._onReminderOutsideClick, true)
            }
        },
    },
    methods: {
        createFormData(note) {
            if (!note) return {content: '', color: null, category_id: null, tag_ids: [], visibility_id: 1, edit_permission_id: 1, viewer_ids: [], editor_ids: []}
            return {
                content: note.content || '',
                color: note.color || null,
                category_id: note.category?.id || null,
                tag_ids: (note.tags || []).map(t => t.id),
                visibility_id: note.visibility?.id || 1,
                edit_permission_id: note.edit_permission?.id || 1,
                viewer_ids: (note.viewers || []).map(v => v.id),
                editor_ids: (note.editors || []).map(e => e.id),
            }
        },
        async handleSave() {
            this.validationErrors = {}
            let hasError = false
            if (this.formData.visibility_id === 3 && (!this.formData.viewer_ids || this.formData.viewer_ids.length === 0)) {
                this.validationErrors.viewer = true
                hasError = true
            }
            if (this.formData.edit_permission_id === 3 && (!this.formData.editor_ids || this.formData.editor_ids.length === 0)) {
                this.validationErrors.editor = true
                hasError = true
            }
            if (hasError) {
                if (!this.showSettings) this.showSettings = true
                return
            }
            this.saving = true
            try {
                if (this.$refs.ckeditor) {
                    this.formData.content = this.$refs.ckeditor.getData()
                }
                const pendingUploads = this.$refs.ckeditor?.getPendingUploads() || []
                this.$emit('save', {id: this.note.id, formData: {...this.formData}, pendingUploads})
            } finally {
                setTimeout(() => {
                    this.saving = false
                }, 500)
            }
        },
        disablePastDate(date) {
            const today = new Date()
            today.setHours(0, 0, 0, 0)
            return date.getTime() < today.getTime()
        },
        disableReminderDate(date) {
            const today = new Date()
            today.setHours(0, 0, 0, 0)
            if (date.getTime() < today.getTime()) return true
            if (this.reminderEventAt) {
                const eventDate = new Date(this.reminderEventAt)
                eventDate.setHours(0, 0, 0, 0)
                return date.getTime() > eventDate.getTime()
            }
            return false
        },
        handleReminderSave() {
            this.$emit('reminderSave', {id: this.note.id, event_at: this.reminderEventAt, reminder_at: this.reminderReminderAt})
            this.showReminderPopover = false
        },
        handleReminderDelete() {
            this.$emit('reminderDelete', {id: this.note.id})
            this.showReminderPopover = false
        },
        previewRevision(rev) {
            this.revisionPreview = rev
        },
        onLiveUpdate(content) {
            if (this.note?._isTemp) {
                this.$emit('draftUpdate', {id: this.note.id, formData: {...this.formData, content}})
            }
        },
        scrollToSettings() {
            this.showSettings = true
        },
        _onReminderOutsideClick(e) {
            const target = e.target
            if (target.closest('.note-reminder-popper') || target.closest('.dtp-overlay') || target.closest('.dtp-panel')) return
            if (this.$refs.reminderBtn && this.$refs.reminderBtn.contains(target)) return
            this.showReminderPopover = false
        },
    },
    beforeUnmount() {
        document.removeEventListener('mousedown', this._onReminderOutsideClick, true)
    },
}
</script>

<style scoped>
.note-editor {
    display: flex;
    flex-direction: column;
    height: 100%;
    background: var(--panel-bg, #ffffff);
}


.note-editor-empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px;
    text-align: center;
}

.note-editor-empty-icon { font-size: 48px; margin-bottom: 16px; }
.note-editor-empty-title { font-size: 16px; font-weight: 500; color: var(--panel-text-primary, #1c1c1e); margin-bottom: 8px; }
.note-editor-empty-desc { font-size: 13px; color: var(--panel-text-secondary, #8e8e93); }

.note-editor-loading {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.note-editor-loading-toolbar {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    border-bottom: 1px solid var(--panel-border, #e5e5ea);
    flex-shrink: 0;
}

.note-editor-loading-pill {
    height: 22px;
    border-radius: 6px;
    background: var(--note-skeleton, rgba(0, 0, 0, 0.06));
    animation: noteEditorShimmer 1.6s ease-in-out infinite;
}

.note-editor-loading-pill:nth-child(2) { animation-delay: 0.1s; }
.note-editor-loading-pill:nth-child(3) { animation-delay: 0.2s; }

.note-editor-loading-body {
    flex: 1;
    padding: 24px 13px;
    display: flex;
    flex-direction: column;
}

.note-editor-loading-line {
    height: 10px;
    border-radius: 5px;
    background: var(--note-skeleton, rgba(0, 0, 0, 0.06));
    margin-bottom: 14px;
    animation: noteEditorShimmer 1.6s ease-in-out infinite;
}

.note-editor-loading-line--title {
    height: 16px;
    border-radius: 6px;
    margin-bottom: 0;
    background: var(--note-skeleton-strong, rgba(0, 0, 0, 0.09));
}

.note-editor-loading-gap {
    height: 10px;
}

.note-editor-loading-body .note-editor-loading-line:nth-child(1) { animation-delay: 0s; }
.note-editor-loading-body .note-editor-loading-line:nth-child(3) { animation-delay: 0.08s; }
.note-editor-loading-body .note-editor-loading-line:nth-child(4) { animation-delay: 0.16s; }
.note-editor-loading-body .note-editor-loading-line:nth-child(5) { animation-delay: 0.24s; }
.note-editor-loading-body .note-editor-loading-line:nth-child(6) { animation-delay: 0.32s; }
.note-editor-loading-body .note-editor-loading-line:nth-child(8) { animation-delay: 0.40s; }
.note-editor-loading-body .note-editor-loading-line:nth-child(9) { animation-delay: 0.48s; }
.note-editor-loading-body .note-editor-loading-line:nth-child(10) { animation-delay: 0.56s; }

@keyframes noteEditorShimmer {
    0%, 100% { opacity: 0.6; }
    50% { opacity: 1; }
}


.note-editor-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 10px;
    border-bottom: 1px solid var(--panel-border, #e5e5ea);
    flex-shrink: 0;
    gap: 6px;
    min-height: 42px;
}

.note-editor-toolbar-left {
    display: flex;
    align-items: center;
    gap: 6px;
    flex: 1;
    min-width: 0;
    overflow: hidden;
}

.note-editor-toolbar-right {
    display: flex;
    align-items: center;
    gap: 1px;
    flex-shrink: 0;
}


.toolbar-separator {
    width: 1px;
    height: 18px;
    background: var(--panel-border, #e5e5ea);
    flex-shrink: 0;
}


.toolbar-chip {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 4px 6px;
    border: 1px solid var(--panel-border, #e5e5ea);
    border-radius: 6px;
    background: transparent;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s;
    flex-shrink: 0;
}

.toolbar-chip:hover {
    border-color: var(--panel-text-secondary, #8e8e93);
    background: var(--note-item-hover, #f5f5f5);
}

.toolbar-chip-color {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
}

.toolbar-chip-color--none {
    background: var(--panel-border, #e5e5ea);
}

.toolbar-chip-arrow {
    color: var(--panel-text-secondary, #8e8e93);
    flex-shrink: 0;
}


.toolbar-select {
    min-width: 100px;
    max-width: 160px;
    flex: 1;
}


.toolbar-icon-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    border: none;
    background: transparent;
    border-radius: 6px;
    cursor: pointer;
    color: var(--panel-text-secondary, #8e8e93);
    transition: background 0.15s, color 0.15s;
}

.toolbar-icon-btn:hover {
    background: var(--panel-selected-bg);
    color: var(--panel-text-primary);
}

.toolbar-icon-btn--active {
    background: var(--panel-selected-bg);
    color: var(--panel-accent-text);
}

.toolbar-icon-btn--pinned {
    color: var(--panel-accent-text);
}

.toolbar-icon-btn--reminder-active {
    color: var(--panel-accent-text);
}

.reminder-icon-wrapper {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
}

.reminder-dot {
    position: absolute;
    top: -2px;
    right: -2px;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--panel-accent);
}

.toolbar-icon-btn--delete:hover {
    background: rgba(255, 59, 48, 0.08);
    color: #FF3B30;
}


.note-editor-content {
    flex: 1;
    overflow-y: auto;
    position: relative;
    display: flex;
    flex-direction: column;
}

.note-editor-content > :deep(*) { flex: 1; display: flex; flex-direction: column; }
.note-editor-content :deep(.ck-editor) { border: none; display: flex; flex-direction: column; flex: 1; }
.note-editor-content :deep(.ck-editor__main) { flex: 1; display: flex; flex-direction: column; }
.note-editor-content :deep(.ck.ck-editor__editable:not(.ck-editor__nested-editable)) { min-height: 200px; flex: 1; border: none !important; box-shadow: none !important; }
.note-editor-content :deep(.ck.ck-toolbar) { border: none !important; border-bottom: 1px solid var(--panel-border, #e5e5ea) !important; }
.note-editor-content :deep(.ck .ck-content) { max-height: none; }

.note-editor-readonly-badge {
    position: absolute;
    top: 8px;
    right: 8px;
    background: var(--panel-border, #e5e5ea);
    color: var(--panel-text-secondary, #8e8e93);
    padding: 2px 8px;
    border-radius: 4px;
    font-size: 11px;
    z-index: 5;
}

.note-editor-html-view {
    padding: 16px;
    font-size: 14px;
    color: var(--panel-text-primary, #1c1c1e);
    line-height: 1.6;
}


.note-editor-settings {
    border-top: 1px solid var(--panel-border, #e5e5ea);
    padding: 12px 16px;
    max-height: 300px;
    overflow-y: auto;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 14px;
    container-type: inline-size;
}

.segmented-label-short { display: none; }

@container (max-width: 450px) {
    .segmented-label-full { display: none; }
    .segmented-label-short { display: inline; }
    .settings-segmented-btn svg { display: none; }
    .settings-segmented-btn { padding: 6px 6px; font-size: 11px; gap: 3px; }
}

.settings-section { display: flex; flex-direction: column; gap: 8px; }

.settings-section--revisions {
    border-top: 1px solid var(--panel-border, #e5e5ea);
    padding-top: 12px;
}

.settings-section-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 600;
    color: var(--panel-text-secondary, #8e8e93);
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.settings-section-label svg { flex-shrink: 0; }

.settings-section-label--clickable { cursor: pointer; justify-content: space-between; }

.settings-section-label-left { display: flex; align-items: center; gap: 6px; }

.revisions-toggle { font-size: 10px; }


.settings-segmented {
    display: flex;
    border-radius: 8px;
    background: var(--note-item-hover, #f5f5f5);
    padding: 3px;
    gap: 2px;
}

.settings-segmented--disabled { opacity: 0.5; pointer-events: none; }

.settings-segmented-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    padding: 8px 10px;
    border: none;
    background: transparent;
    border-radius: 6px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 500;
    color: var(--panel-text-secondary, #8e8e93);
    transition: all 0.2s ease;
    white-space: nowrap;
}

.settings-segmented-btn svg { flex-shrink: 0; }

.settings-segmented-btn:hover:not(.active) { color: var(--panel-text-primary, #1c1c1e); }

.settings-segmented-btn.active {
    background: var(--panel-bg, #ffffff);
    color: var(--panel-accent-text);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 0 0 1px rgba(0, 0, 0, 0.04);
}

html.dark .settings-segmented-btn.active {
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.06);
}

.settings-sub-select { padding-left: 4px; margin-top: 2px; }
.settings-sub-select .el-select { width: 100%; }
.settings-sub-select :deep(.el-input__wrapper) { padding: 4px 11px; }
.settings-sub-select :deep(.el-select__tags) { gap: 2px; }
.settings-select--error :deep(.el-input__wrapper) { box-shadow: 0 0 0 1px #f56c6c !important; }
.settings-validation-error { color: #f56c6c; font-size: 11px; margin-top: 6px; padding-left: 2px; }


.editor-revisions-list { margin-top: 2px; }

.editor-revision-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 10px;
    font-size: 12px;
    cursor: pointer;
    border-radius: 6px;
    color: var(--panel-text-secondary, #8e8e93);
}

.editor-revision-item:hover { background: var(--panel-selected-bg); }

.revision-date { font-weight: 500; }


.note-editor-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 8px 12px;
    border-top: 1px solid var(--panel-border, #e5e5ea);
    flex-shrink: 0;
}

.editor-save-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 24px;
    background: var(--panel-accent);
    color: #fff;
    border: none;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: opacity 0.15s;
}

.editor-save-btn:hover { opacity: 0.85; }
.editor-save-btn:disabled { opacity: 0.6; cursor: not-allowed; }

.editor-save-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: #fff;
    border-radius: 50%;
    animation: saveSpinnerRotate 0.6s linear infinite;
}

@keyframes saveSpinnerRotate {
    to { transform: rotate(360deg); }
}


.note-revision-modal-content {
    display: flex;
    flex-direction: column;
}

.note-revision-modal-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 18px 20px 14px;
    border-bottom: 1px solid #e5e5ea;
    gap: 12px;
}

.note-revision-modal-header-left {
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-width: 0;
}

.note-revision-modal-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 600;
    color: #1c1c1e;
}

.note-revision-modal-title svg {
    color: var(--panel-accent-text);
    flex-shrink: 0;
}

.note-revision-modal-meta {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 12px;
    font-weight: 500;
    color: #636366;
}

.note-revision-modal-meta svg {
    color: #8e8e93;
    flex-shrink: 0;
}

.note-revision-modal-meta-text {
    margin-right: 2px;
}

.note-revision-modal-meta-dot {
    width: 3px;
    height: 3px;
    border-radius: 50%;
    background: #c7c7cc;
    flex-shrink: 0;
    margin: 0 4px;
}

.note-revision-modal-close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    background: #f2f2f7;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    color: #8e8e93;
    flex-shrink: 0;
    transition: background 0.15s, color 0.15s;
}

.note-revision-modal-close:hover {
    background: #e5e5ea;
    color: #1c1c1e;
}

.note-revision-modal-body {
    padding: 20px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.note-revision-modal-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.note-revision-modal-field-label {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    color: #8e8e93;
}

.note-revision-modal-field-value {
    font-size: 14px;
    line-height: 1.5;
    color: #1c1c1e;
}

.note-revision-modal-field-value--html {
    line-height: 1.7;
}

html.dark .note-revision-modal-header {
    border-bottom-color: #26272F;
}

html.dark .note-revision-modal-title {
    color: #F5F5F5;
}

html.dark .note-revision-modal-meta {
    color: #9A9CAE;
}

html.dark .note-revision-modal-meta svg {
    color: #636674;
}

html.dark .note-revision-modal-meta-dot {
    background: #464852;
}

html.dark .note-revision-modal-close {
    background: #26272F;
    color: #9A9CAE;
}

html.dark .note-revision-modal-close:hover {
    background: #32333D;
    color: #F5F5F5;
}

html.dark .note-revision-modal-field-label {
    color: #636674;
}

html.dark .note-revision-modal-field-value {
    color: #F5F5F5;
}

html.midnight .note-revision-modal-header {
    border-bottom-color: #2C2823;
}

html.midnight .note-revision-modal-title {
    color: #EAE6E1;
}

html.midnight .note-revision-modal-meta {
    color: #A9A39D;
}

html.midnight .note-revision-modal-meta svg {
    color: #6B655E;
}

html.midnight .note-revision-modal-meta-dot {
    background: #504A43;
}

html.midnight .note-revision-modal-close {
    background: #2C2823;
    color: #A9A39D;
}

html.midnight .note-revision-modal-close:hover {
    background: #3D3832;
    color: #EAE6E1;
}

html.midnight .note-revision-modal-field-label {
    color: #6B655E;
}

html.midnight .note-revision-modal-field-value {
    color: #EAE6E1;
}

.reminder-popover {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.reminder-popover-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--panel-text-primary, #1c1c1e);
}

.reminder-popover-field {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.reminder-popover-field label {
    font-size: 11px;
    font-weight: 500;
    color: var(--panel-text-secondary, #8e8e93);
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.reminder-popover-actions {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-top: 4px;
}

.reminder-save-btn {
    padding: 6px 12px;
    background: var(--panel-accent);
    color: #fff;
    border: none;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s;
}

.reminder-save-btn:hover { opacity: 0.85; }
.reminder-save-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.reminder-delete-btn {
    padding: 6px 12px;
    background: transparent;
    color: #FF3B30;
    border: 1px solid #FF3B30;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s;
}

.reminder-delete-btn:hover { background: rgba(255, 59, 48, 0.08); }

.note-editor-reminder-panel {
    border-top: 1px solid var(--panel-border, #e5e5ea);
    padding: 12px 16px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex-shrink: 0;
}


.reminder-panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.reminder-panel-title {
    font-size: 13px;
    font-weight: 600;
    color: var(--panel-text-primary, #1c1c1e);
}

.reminder-panel-close {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border: none;
    background: transparent;
    border-radius: 4px;
    cursor: pointer;
    color: var(--panel-text-secondary, #8e8e93);
}

.reminder-panel-close:hover {
    background: var(--note-item-hover, #f5f5f5);
}


</style>
